import { defineAsyncComponent, reactive, toRaw } from "vue";
import {
    createAppPageHeaderTop,
    createManageActive,
    createManageArchive,
    createManageExportCsv,
    createManageExtraCreate,
    createManageSelect,
    createPage,
    createRowActionEdit,
    createRowActionView,
} from "../../../../../utils/page";
import { createColumns, createFields } from "./fishing-tool.section";
import {
    onClickActiveAll,
    onClickArchiveAll,
    onClickCreate,
    onClickDelete,
    onClickEdit,
    onClickView,
    checkAttribute,
    initUseForm,
    onClickActive,
    onClickArchive,
} from "../../../../../utils/form";
import { createConditionAction, createConditionForm } from "./fishing-tool.condition";
import { setAppPageHeader } from "../../../../../components/layout/app-layout/app-layout.logic";
import store from "../../../../../store";
import { createLoading } from "../../../../../utils/loading";
import { required, validateError } from "../../../../../utils/validate";
import { addModal, setIsLoading as setIsLoadingApp } from "../../../../../app.logic";
import i18n from "../../../../../i18n";
import { useForm as createUseForm } from "@ant-design-vue/use";
import { Col, Row } from "ant-design-vue";
import { has } from "../../../../../utils/menu-context";
import {
    createButtonActive,
    createButtonArchive,
    createButtonCreate,
    createButtonDelete,
    createButtonEdit,
    createButtonSave,
    createButtonView,
} from "../../../../../utils/button";
import lodash from "lodash";
import { modalStyleForm, modalStyleIFrame } from "../../../../../utils/constant";
import { createWhereFor } from "../../../user-request/request-register/user-request-register-approve.logic";

const FishingToolForm = defineAsyncComponent(() => import("./FishingToolForm"));

export const { isLoading, setIsLoading } = createLoading();

export const actionPrefix = "resource/v1/master/fishingTool";
export const routerPrefix = "fishing-tools";

export const callSave = ({ form }) => store.dispatch(`${actionPrefix}/save`, { data: form });
export const callSearch = ({ criteria }) => store.dispatch(`${actionPrefix}/search`, { criteria });
export const callActive = ({ id }) => store.dispatch(`${actionPrefix}/active`, { id });
export const callArchive = ({ id }) => store.dispatch(`${actionPrefix}/delete`, { id });
export const callDelete = ({ id }) => store.dispatch(`${actionPrefix}/delete`, { id, config: { params: { isHardDelete: true } } });
export const callActiveAll = ({ ids }) => store.dispatch(`${actionPrefix}/activeAll`, { ids });
export const callArchiveAll = ({ ids }) => store.dispatch(`${actionPrefix}/deleteAll`, { ids });
export const callDeleteAll = ({ ids }) => store.dispatch(`${actionPrefix}/deleteAll`, { ids, config: { params: { isHardDelete: true } } });
export const callChanges = ({ id, criteria }) => store.dispatch(`${actionPrefix}/changes`, { id, criteria });

const CIFrame = defineAsyncComponent(() => import("../../../../../components/iframe/CIFrame"));

export const onSearch = ({ page, props }) => {
    const where = page.where || [];
    const sort = page.sort || [];
    setIsLoading(true);
    window.onSearch?.({ page });
    return callSearch({
        criteria: {
            ...page,
            data: [],
            where: [...where, ...createWhereFor({ props }), { name: "refParent.id", type: "UUID", operator: "=", value: null }],
        },
    })
        .then((page) => (state.page = { ...page, where, sort }))
        .finally(() => setIsLoading(false));
};

export const onClickSave = ({ props, data, refs, formParams }) => {
    const { viewOnly, routerPrefix } = props;
    return data.useForm
        .validate()
        .then(() => {
            setIsLoadingApp(true);
            return callSave({ form: { ...data.useForm.modelRef, ...formParams } })
                .finally(() => setIsLoadingApp(false))
                .then((record) => {
                    if (viewOnly) initForm({ props, data, refs });
                    else onClickView({ record, routerPrefix });
                });
        })
        .catch((error) => {
            validateError({ error });
            throw error;
        });
};

export const onClickModalOk = ({ data }) => {
    return data.useForm
        .validate()
        .then(() => ({ ...toRaw(data.useForm.modelRef), isSave: true }))
        .catch((error) => {
            validateError({ error });
            throw error;
        });
};

export const createSearchOneCriteria = ({ id, forLog }) => {
    if (!forLog) {
        return {
            select: ["**"],
            page: 0,
            size: 1,
            where: [{ name: "id", type: "UUID", operator: "=", value: id }],
        };
    } else {
        return {
            select: [],
            page: 0,
            size: 1,
            where: [{ name: "id", type: "UUID", operator: "=", value: id }],
        };
    }
};

export const createForm = () => {
    return {
        code: null,
        nameTh: null,
        nameEn: null,
        refParent: null,
        refFishingToolUnit: null,
        refWidthType: null,
        isMaximumWidth: false,
        isRequireInputWidth: false,
        isMaximumQty: false,
        isRequireInputQty: false,
        isCountLimitRequest: false,
        dutyPerUnit: null,
        seq: null,
        children: [],
    };
};

export const createRules = ({ key }) => {
    const prefix = key ? `${key}.` : "";
    return {
        [`${prefix}nameTh`]: [required],
        [`${prefix}refFishingToolUnit`]: [required],
    };
};

export const createFormTitle = ({ data }) => {
    const modelRef = data.useForm.modelRef;
    return modelRef.id ? `${modelRef.nameTh}` : i18n.global.t("button.create");
};

export const initForm = ({ props, data, refs }) => {
    const { routerPrefix } = props;
    checkAttribute({ props, data, refs });
    data.form = createForm({ props, data, refs });
    data.rules = createRules({ props, data, refs });
    data.useForm = createUseForm(data.form, data.rules);
    data.fields = createFields({ props, data, refs, isLoading });
    data.conditionForms = createConditionForm({ props, data, refs, isLoading });
    if (data.id) {
        data.children = {
            fetchCriteria: {
                select: ["*", "refParent.*", "refParent.refAddr.**"],
                page: 0,
                size: 1000000,
                where: [{ name: "refParent.id", type: "UUID", operator: "=", value: data.id }],
            },
        };
    }
    loadForm({ props, data, refs }).then(() => {
        if (props.setAppPageHeader) {
            setAppPageHeader({
                slots: {
                    top: () => createAppPageHeaderTop({ props, data, refs, state, routerPrefix, onClickView, onClickEdit }),
                    default: () => createAppPageHeaderDefault({ props, data, refs }),
                },
            });
        }
    });
};

export const createAppPageHeaderDefault = ({ props, data, refs }) => {
    const col1 = [];
    const col2 = [];
    const { menuContext, viewOnly, routerPrefix } = props;
    const { useForm } = data;
    const { modelRef, initialModel } = useForm;
    const { id, isActive } = modelRef;
    const canEdit = state.conditionAction.canEdit({ props, record: initialModel });
    if (id && has({ menuContext, key: "canCreate" })) {
        col1.push(createButtonCreate({ onClick: () => onClickCreate({ routerPrefix }), classStyle: "m-1" }));
    }
    if (!viewOnly && id && has({ menuContext, key: "canView" })) {
        col1.push(createButtonView({ onClick: () => onClickView({ record: { id }, routerPrefix }), classStyle: "m-1" }));
    }
    if (viewOnly && id && isActive && canEdit) {
        col1.push(createButtonEdit({ onClick: () => onClickEdit({ record: { id }, routerPrefix }), classStyle: "m-1" }));
    }
    if (!viewOnly && (!id || (id && isActive && canEdit))) {
        col1.push(createButtonSave({ onClick: () => onClickSave({ props, data, refs }), classStyle: "m-1" }));
    }
    if (viewOnly && id && !isActive && has({ menuContext, key: "canActive" })) {
        col2.push(
            createButtonActive({
                onClick: () =>
                    onClickActive({
                        record: { id },
                        callActive,
                        setIsLoading: setIsLoadingApp,
                        onClickEdit: () => onClickEdit({ record: { id }, routerPrefix }),
                    }),
                classStyle: "m-1",
            })
        );
    }
    if (!viewOnly && id && isActive && has({ menuContext, key: "canArchive" })) {
        col2.push(
            createButtonArchive({
                onClick: () =>
                    onClickArchive({
                        record: { id },
                        callArchive,
                        setIsLoading: setIsLoadingApp,
                        onClickView: () => onClickView({ record: { id }, routerPrefix }),
                    }),
                classStyle: "m-1",
            })
        );
    }
    if (!viewOnly && id && isActive && has({ menuContext, key: "canDelete" })) {
        col2.push(
            createButtonDelete({
                onClick: () => onClickDelete({ record: { id }, callDelete, setIsLoading: setIsLoadingApp, state, routerPrefix }),
                classStyle: "m-1",
            })
        );
    }
    return (
        <Row type="flex">
            <Col>{col1}</Col>
            <Col class="ml-auto">{col2}</Col>
        </Row>
    );
};

export const loadForm = ({ props, data, refs }) => {
    setIsLoading(true);
    const { form } = props;
    const { id, useForm } = data;
    const { modelRef } = useForm;
    let promise = null;
    if (form) {
        promise = Promise.resolve({ data: [form] });
    } else if (id) {
        promise = callSearch({ criteria: createSearchOneCriteria({ id }) });
    } else {
        promise = Promise.resolve({ data: [useForm.modelRef] });
    }
    return promise
        .then((page) => initUseForm({ props, data, refs, page }))
        .then(() => loadDefaultForm({ props, data, refs }))
        .then(() => initUseForm({ props, data, refs, page: { data: [modelRef] } }))
        .finally(() => setIsLoading(false));
};

export const loadDefaultForm = () => {
    const promises = [];
    return Promise.all(promises);
};

export const initState = ({ props }) => {
    const { menuContext } = props;
    state.manages = [
        createManageSelect({ menuContext }),
        createManageActive({
            menuContext,
            onClick: ({ rowSelection }) =>
                onClickActiveAll({ rowSelection, callActiveAll, state, onSearch: (search) => onSearch({ props, ...search }), setIsLoading }),
        }),
        createManageArchive({
            menuContext,
            onClick: ({ rowSelection }) =>
                onClickArchiveAll({ rowSelection, callArchiveAll, state, onSearch: (search) => onSearch({ props, ...search }), setIsLoading }),
        }),
        createManageExportCsv({ menuContext }),
    ];
    state.manageExtras = [createManageExtraCreate({ menuContext, onClick: () => onClickCreate({ routerPrefix }) })];
    state.rowActions = [
        createRowActionView({ menuContext, onClick: ({ record }) => onClickView({ record, routerPrefix }) }),
        createRowActionEdit({
            menuContext,
            onClick: ({ record }) => onClickEdit({ record, routerPrefix }),
            visible: ({ record }) => state.conditionAction.canEdit({ props, record }),
        }),
    ];
};

export const state = reactive({
    columns: createColumns(),
    page: createPage({
        options: {
            select: ["**"],
        },
    }),
    tableOptions: { search: { activeKey: "Search" } },
    conditionAction: createConditionAction(),
});

export const openModalList = ({ bind, onIframeMounted }) => {
    const options = addModal({
        bind: lodash.merge(lodash.cloneDeep(modalStyleIFrame), bind),
        render: () => (
            <CIFrame
                src={`${process.env.VUE_APP_PUBLIC_PATH}/app/data-mgt/fishing-tools?isLayout=false&canSelect=true&cacheQuery=true`}
                onIframeMounted={({ refs }) => onIframeMounted({ options, refs })}
            ></CIFrame>
        ),
    });
    return options;
};
export const openModalForm = ({ props, items }) => {
    const refs = reactive({ form: null });
    const options = addModal({
        bind: lodash.merge(lodash.cloneDeep(modalStyleForm), {
            okButtonProps: { disabled: props.viewOnly },
            header: i18n.global.t("model.fishingTool.children"),
            onOk: () => {
                refs.form.callOnClickModalOk().then((record) => {
                    if (items) {
                        items.push(record);
                    } else {
                        lodash.merge(props.form, record);
                    }
                    options.bind.onCancel();
                });
            },
        }),
        render: () => (
            <FishingToolForm
                ref={(e) => {
                    if (e) refs.form = e;
                }}
                {...props}
            ></FishingToolForm>
        ),
    });
    return options;
};
